import { useState, ChangeEvent, useEffect } from 'react';
import { Card, Table, Button, Input, Dropdown, App as AntdApp } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EllipsisVertical, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { User } from '../types';
import AddUser from './Forms/AddUser';
import { CreateUserFormData, EditUserFormData } from '../types/user';
import PasswordModal from './PasswordModal';
import { generatePassword } from '../utils';
import { useAppStore } from '../store/appStore';
import { useCreateUser, useDeleteUser, useEditUser, useUsers } from '../hooks/useUser';
import UserManagementSkeleton from './Skeleton/UserManagement';
import ErrorFallback from './ErrorFallback';
import EditUser from './Forms/EditUser';

interface EditUser {
  isVisible: boolean;
  data: User;
}

const UserManagement = () => {
  const { message } = AntdApp.useApp();
  const [searchText, setSearchText] = useState<string>('');
  const [filteredData, setFilteredData] = useState<User[]>();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<EditUser>({
    isVisible: false,
    data: {} as User,
  });
  const [generatedPassword, setGeneratedPassword] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);

  const roles = useAppStore((state) => state.roles);
  const plants = useAppStore((state) => state.plants);

  const { data: users, isLoading, error, refetch } = useUsers();
  const { mutate: createUser } = useCreateUser();
  const { mutate: updateUser } = useEditUser();
  const { mutate: deleteUser } = useDeleteUser();

  useEffect(() => {
    setFilteredData(users);
  }, [users]);

  console.log(users, isLoading, error);

  // Search functionality
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    if (users) {
      const filtered = users.filter(
        (employee) =>
          employee.name.toLowerCase().includes(value) ||
          employee.email.toLowerCase().includes(value) ||
          employee.role.roleName.toLowerCase().includes(value) ||
          employee.plant.name.toLowerCase().includes(value)
      );

      setFilteredData(filtered);
    }
  };

  // Create new user handler
  const handleCreateUser = () => {
    setIsCreateModalVisible(true);
  };

  const actionMenu = (record: User) => ({
    items: [
      // {
      //   key: 'show',
      //   label: (
      //     <div className="flex items-center gap-2">
      //       <Eye size={16} />
      //       <span>Show Password</span>
      //     </div>
      //   ),
      //   onClick: () => {
      //     console.log('Show Password:', record);
      //     setIsPasswordModalVisible(true);
      //   },
      // },
      {
        key: 'edit',
        label: (
          <div className="flex items-center gap-2">
            <Pencil size={16} />
            <span>Edit</span>
          </div>
        ),
        onClick: () => {
          console.log('Edit:', record);
          setEditUser({ isVisible: true, data: record });
        },
      },
      {
        key: 'delete',
        label: (
          <div className="flex items-center gap-2 text-red-500">
            <Trash2 size={16} />
            <span>Delete</span>
          </div>
        ),
        onClick: () => deleteUser(record.id),
      },
    ],
  });

  // Table column definitions
  const columns: ColumnsType<User> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Role',
      key: 'role',
      filters: roles.map((role:any) => ({ text: role.roleName, value: role.roleName })),
      onFilter: (value, record) => record?.role?.roleName?.startsWith(value as string),
      render: (_, record) => record?.role?.roleName || 'N/A',
    },
    {
      title: 'Plant',
      key: 'plant',
      filters: plants.map((plant:any) => ({ text: plant.name, value: plant.name })),
      onFilter: (value, record) => record?.plant?.name?.startsWith(value as string),
      render: (_, record) => record?.plant?.name || 'N/A',
    },

    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'right',
      render: (_: unknown, record: User) => (
        <Dropdown menu={actionMenu(record)} trigger={['click']} placement="bottomRight">
          <Button type="text" className="hover:bg-gray-100 rounded-full">
            <EllipsisVertical size={18} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  // Create User
  const handleAdduser = async (values: CreateUserFormData) => {
    try {
      // Generate password for the new user
      const password = generatePassword();
      setGeneratedPassword(password);

      const data = { ...values, password };

      // Here you would typically send the user data to your API
      console.log('Created user:', {
        ...values,
        password,
      });
      setIsCreateModalVisible(false);
      // Only open password modal if API call is successful
      createUser(data, {
        onSuccess: () => {
          setIsCreateModalVisible(false);
          setIsPasswordModalVisible(true);
          message.success('User created successfully!');
        },
        onError: (error) => {
          message.error(`Failed to create user: ${error.message}`);
          // Don't open password modal on error
        },
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleUpdateUser = (values: EditUserFormData) => {
    console.log(values);
    updateUser({ id: editUser?.data?.id, data: values });
  };

  if (isLoading) return <UserManagementSkeleton />;
  if (error) return <ErrorFallback error={error} refetch={refetch} />;
  return (
    <>
      <Card>
        <div className="flex flex-col md:flex-row md:justify-between md:items-center !mb-4">
          <div className="w-full md:w-1/5 !mb-4 md:mb-0 ">
            <Input
              placeholder="Search users..."
              prefix={<Search className="w-5 h-5 text-secondary" />}
              value={searchText}
              onChange={handleSearch}
              allowClear
            />
          </div>
          <Button type="primary" icon={<Plus />} onClick={handleCreateUser}>
            Create New User
          </Button>
        </div>

        <Table<User>
          dataSource={filteredData}
          columns={columns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users`,
          }}
          rowKey="email"
          className="overflow-x-auto"
        />
        <AddUser
          isCreateModalVisible={isCreateModalVisible}
          setIsCreateModalVisible={setIsCreateModalVisible}
          addUser={handleAdduser}
        />
        <PasswordModal
          isPasswordModalVisible={isPasswordModalVisible}
          setIsPasswordModalVisible={setIsPasswordModalVisible}
          generatedPassword={
            generatedPassword.length === 0 ? 'Pgp@factoryViewAR123' : generatedPassword
          }
          copied={copied}
          setCopied={setCopied}
        />
      </Card>

      <EditUser
        isEditModalVisible={editUser.isVisible}
        setIsEditModalVisible={(visible) => setEditUser({ isVisible: visible, data: {} as User })}
        handleUpdateUser={handleUpdateUser}
        initialData={editUser.data}
      />
    </>
  );
};

export default UserManagement;
